import {
  Side,
  bracketTag,
  isWorkingOrder,
  positionOpenedNs,
  type AccountFacts,
  type FillEvent,
  type FillFacts,
  type OrderEvent,
  type OrderFacts,
  type PositionFacts,
} from "@helm/core";
import type { PriceFormat } from "@helm/ui";
import { marketData, priceFormatOf } from "../md/marketData";
import type { AccountList, TradingView } from "../viewmodel/account";

/**
 * Trading view model → the rows the column registries read (`@helm/core` tables). Pure apart
 * from the md lookups for price formats.
 */

export function accountLabelOf(tv: { accounts: AccountList }, id: string): string {
  return tv.accounts.find((a) => a.summary.id === id)?.summary.label ?? id;
}

/** Contracts bought / sold per `account\u001fsymbol` over the fills in view. */
function tradedQty(fills: readonly FillEvent[]): Map<string, { bought: number; sold: number }> {
  const m = new Map<string, { bought: number; sold: number }>();
  for (const f of fills) {
    const k = `${f.accountId}\u001f${f.symbol}`;
    const e = m.get(k) ?? { bought: 0, sold: 0 };
    if (f.side === Side.BUY) e.bought += f.qty;
    else if (f.side === Side.SELL) e.sold += f.qty;
    m.set(k, e);
  }
  return m;
}

export function positionFacts(
  tv: TradingView,
  rows: TradingView["positions"],
  nowMs: number,
): PositionFacts[] {
  const traded = tradedQty(tv.fills);
  return rows.map((p) => {
    const opened = positionOpenedNs(tv.fills, p.accountId, p.symbol, p.netQty);
    const t = traded.get(`${p.accountId}\u001f${p.symbol}`);
    return {
      key: p.key,
      accountId: p.accountId,
      accountLabel: accountLabelOf(tv, p.accountId),
      symbol: p.symbol,
      netQty: p.netQty,
      openCostTicks: p.position.openCostTicks,
      exactCost: p.exactCost,
      markTicks: p.markTicks,
      tickValueCents: p.info && p.info.tickValueCents > 0 ? p.info.tickValueCents : null,
      realizedCents: p.realizedCents,
      // No fee feed yet (ADR-0011 item 8): fills carry 0 marked "estimate".
      feesCents: null,
      excursion: tv.excursions.get(p.key) ?? null,
      openedMs: opened === null ? null : Number(opened / 1_000_000n),
      nowMs,
      // Account drawdown is unavailable until verified broker coverage exists.
      risk: null,
      boughtQty: t?.bought ?? 0,
      soldQty: t?.sold ?? 0,
      bracket: bracketCover(tv.orders, p.accountId, p.symbol),
    };
  });
}

/** Working bracket children of (account, symbol): contracts under stops / targets, and legs. */
function bracketCover(
  orders: readonly OrderEvent[],
  accountId: string,
  symbol: string,
): { stopQty: number; targetQty: number; legs: number } | null {
  let stopQty = 0;
  let targetQty = 0;
  const groups = new Set<string>();
  for (const o of orders) {
    if (o.accountId !== accountId || o.symbol !== symbol || !isWorkingOrder(o)) continue;
    const role = bracketTag(o);
    if (role === null) continue;
    const open = Math.max(0, o.qty - o.filled);
    if (role.startsWith("SL")) stopQty += open;
    else targetQty += open;
    groups.add(o.ocoGroup);
  }
  return groups.size === 0 ? null : { stopQty, targetQty, legs: groups.size };
}

export function accountFacts(tv: TradingView, rows: TradingView["accounts"]): AccountFacts[] {
  return rows.map((a) => ({
    accountId: a.summary.id,
    label: a.summary.label,
    isMain: a.summary.id === tv.mainId,
    status: a.summary.status,
    simulation: a.summary.environment === "simulation",
    pnlPlant: a.pnlPlant,
    balanceCents: a.balanceCents,
    realizedCents: a.realizedCents,
    unrealizedCents: a.unrealizedCents,
    openPositions: a.openPositions,
    workingOrders: a.workingOrders,
    dayPnlCents: a.dayPnlCents,
    cashOnHandCents: a.cashOnHandCents,
    closedPositionPnlCents: a.closedPositionPnlCents,
    financeReason: a.financeReason,
    brokerCurrency: a.brokerCurrency,
    asOf: a.asOf,
    broker: {
      threshold: a.broker.threshold,
      trailingDrawdown: a.broker.trailingDrawdown,
      dailyLoss: a.broker.dailyLoss,
      // Absent ("not computed") while the firm-rules chunk is not loaded (risk/firmGate.ts).
      ...(a.broker.firm ? { targetRoom: a.broker.firm.targetRoom } : {}),
    },
    risk: null,
  }));
}

type OrderRow = OrderFacts & { order: OrderEvent };

/**
 * Row objects keep their identity while their OrderEvent / FillEvent and account label do, so the
 * table's memoized rows re-render only for the order or fill that changed.
 */
const orderRows = new WeakMap<OrderEvent, OrderRow>();
const fillRows = new WeakMap<FillEvent, FillFacts>();

export function orderFacts(tv: { accounts: AccountList }, rows: readonly OrderEvent[]): OrderRow[] {
  return rows.map((o) => {
    const accountLabel = accountLabelOf(tv, o.accountId);
    const hit = orderRows.get(o);
    if (hit?.accountLabel === accountLabel) return hit;
    const row = orderRow(o, accountLabel);
    orderRows.set(o, row);
    return row;
  });
}

function orderRow(o: OrderEvent, accountLabel: string): OrderRow {
  return {
    order: o,
    clientOrderId: o.clientOrderId,
    accountId: o.accountId,
    accountLabel,
    symbol: o.symbol,
    side: o.side,
    type: o.type,
    qty: o.qty,
    filled: o.filled,
    avgFillTicks: o.avgFillTicks,
    limitTicks: o.limitTicks,
    stopTicks: o.stopTicks,
    tif: o.tif,
    state: o.state,
    external: o.external,
    createdNs: o.createdNs,
    updatedNs: o.updatedNs,
    rejectReason: o.rejectReason,
    parentId: o.parentId,
    ocoGroup: o.ocoGroup,
    bracketRole: o.bracketRole,
    bracketLeg: o.bracketLeg,
    origin: o.origin,
  };
}

export function fillFacts(tv: { accounts: AccountList }, rows: readonly FillEvent[]): FillFacts[] {
  return rows.map((f) => {
    const accountLabel = accountLabelOf(tv, f.accountId);
    const hit = fillRows.get(f);
    if (hit?.accountLabel === accountLabel) return hit;
    const row = fillRow(f, accountLabel);
    fillRows.set(f, row);
    return row;
  });
}

function fillRow(f: FillEvent, accountLabel: string): FillFacts {
  return {
    fillId: f.fillId,
    clientOrderId: f.clientOrderId,
    accountId: f.accountId,
    accountLabel,
    symbol: f.symbol,
    side: f.side,
    qty: f.qty,
    priceTicks: f.priceTicks,
    tsNs: f.tsNs,
    feeCents: f.feeCents,
    feeIsEstimate: f.feeIsEstimate,
    external: f.external,
  };
}

/** Price format of a contract ("NQZ6"), null when its instrument is not loaded. */
export function priceFormatForContract(contract: string): PriceFormat | null {
  return priceFormatOf(marketData.findByContract(contract)?.info ?? null);
}
